import * as React from 'react';
import * as styles from './help.module.scss';
import HelpDocument from '../../models/helpDocument';
import useHelp from '../../hooks/useHelp';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { TextTransformProperty, PositionProperty } from 'csstype';
import Paths from '../../models/enums/paths';
import { Redirect, useParams, useHistory } from 'react-router-dom';
import { AppContext } from '../../shared/app-context';
import useMeasuredRef from '../../hooks/useMeasuredRef';
import { Scrollbars } from 'react-custom-scrollbars';
import { createTheme, ThemeProvider } from '@material-ui/core';

interface Props {
  documents: HelpDocument[];
  searchQuery: string;
  isSearching: boolean;
  setIsSearching: (searching: boolean) => void;
  setErrorMessage: (message: string) => void;
}

const dialogStyles = {
  helpDocuments: { width: '100%', border: '1px solid #dadada', overflowY: 'auto' as 'auto' },
  contentHeader: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px 0 30px',
    fontWeight: '600' as 'bold',
    fontSize: '16px',
    borderBottom: '1px solid #8a8a8a',
    justifyContent: 'space-between'
  },
  fullScreenDiv: {
    color: '#006da8',
    fontWeight: '400' as 'normal',
    fontSize: '14px',
    textTransform: 'uppercase' as TextTransformProperty,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  fullScreenText: {
    marginLeft: '2px'
  },
  documentsContainer: {
    margin: '48px 57px',
    position: 'relative' as PositionProperty
  }
};

const materialTheme = createTheme({
  overrides: {
    MuiDialog: {
      root: {
        zIndex: '20000 !important'
      }
    }
  }
});

/**
 * FullScreenDialog
 * @function FullScreenDialog - Full screen dialog to show the contents of a help document
 */
const FullScreenDialog = ({ openFullScreen, setOpenFullScreen, renderHelpContent, documents, activeDocument }) => {
  return (
    <ThemeProvider theme={materialTheme}>
      <Dialog fullScreen open={openFullScreen} onClose={() => setOpenFullScreen(false)}>
        <DialogContent>
          <div style={dialogStyles.helpDocuments}>
            <div style={dialogStyles.contentHeader}>
              <span>Document</span>
              <div onClick={() => setOpenFullScreen(false)} style={dialogStyles.fullScreenDiv}>
                <FullscreenExitIcon />
                <span style={dialogStyles.fullScreenText}>Minimize</span>
              </div>
            </div>
            <div style={dialogStyles.documentsContainer}>{renderHelpContent(documents, activeDocument)}</div>
          </div>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

/**
 * TableOfContents
 * @function TableOfContents - Component for the left toolbar and the help documents page
 */
const TableOfContents = ({ documents, searchQuery, isSearching, setIsSearching, setErrorMessage }: Props) => {
  const { state } = React.useContext(AppContext);
  const { activeDocument, renderHelpContent, renderSidePanel, renderPreviews, setActiveDocument } = useHelp();
  const [openFullScreen, setOpenFullScreen] = React.useState<boolean>(false);
  const [redirect, setRedirect] = React.useState<boolean>(false);
  const { document } = useParams();
  const history = useHistory();

  const announcementCount: number = +!!state.clientAnnouncement + +!!state.globalAnnouncement;
  const { measuredRef, dimensions } = useMeasuredRef([announcementCount], true);

  /** If there is no active document, the first document will become the active document so that there is always a document to display */
  /** If a document is navigated to directly (via url) use param: document to set the active document */
  React.useEffect(() => {
    if (documents.length > 0) {
      if ((activeDocument === -1 && document == undefined) || Number(document) === -1) {
        setActiveDocument(documents[0].snippetGroupId);
        history.replace(`${Paths.Help}/${documents[0].snippetGroupId}`);
      } else if (document != undefined && activeDocument != Number(document)) {
        const currentIndex = documents.findIndex(doc => doc.snippetGroupId === Number(document));
        if (currentIndex != -1) {
          setActiveDocument(Number(document));
        } else {
          setErrorMessage('Help document not found for the current client');
          setActiveDocument(documents[0].snippetGroupId);
          history.replace(Paths.Help);
        }
      } else if (activeDocument && !redirect) {
        setRedirect(true);
      }
    }
  }, [activeDocument, documents.length]);

  if (redirect) {
    setRedirect(false);
    return <Redirect to={`${Paths.Help}/${activeDocument}`} />;
  }

  /** @const previews - Array of JSX elements that displays the preview of the help documents as the user is typing in the search bar */
  const previews = renderPreviews(searchQuery, isSearching, documents, setIsSearching);
  return (
    <div
      className={styles.helpContent}
      ref={measuredRef}
      style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}
    >
      <div className={styles.tableOfContents}>
        <h5 className={styles.tableHeader}>Select a Document</h5>
        <div className={styles.folders}>
          <Scrollbars className={styles.scrollTableOfContents} autoHide={true} universal={true}>
            {renderSidePanel(documents, setIsSearching)}
          </Scrollbars>
        </div>
      </div>
      <div className={styles.helpDocuments}>
        <div className={styles.contentHeader}>
          <h5>Document</h5>
          <div onClick={() => setOpenFullScreen(true)} className={styles.fullScreenText}>
            <FullscreenIcon />
            <span>Maximize</span>
          </div>
        </div>
        <div className={styles.documentsContainer}>
          {previews ? (
            previews
          ) : (
            <Scrollbars className={styles.scrolling} autoHide={true} universal={true} hideTracksWhenNotNeeded={true}>
              {renderHelpContent(documents, activeDocument)}
            </Scrollbars>
          )}
        </div>
        <FullScreenDialog
          openFullScreen={openFullScreen}
          setOpenFullScreen={setOpenFullScreen}
          renderHelpContent={renderHelpContent}
          documents={documents}
          activeDocument={activeDocument}
        />
      </div>
    </div>
  );
};

export default TableOfContents;
